import React from 'react';
import { View } from 'react-native';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Reveal from '@/components/Landing/Reveal';
import {
  CtaRow,
  DisplayHeading,
  GhostCta,
  Kicker,
  Lede,
  PrimaryCta,
  StoreBadges,
  useLanding,
} from '@/components/Landing/LandingKit';
import { LAYOUT, fluid, isWeb } from '@/components/Landing/landingTokens';

/**
 * The closing argument every marketing page ends on.
 *
 * A recessed band so the page arrives somewhere and separates cleanly from the
 * footer beneath. Content is entirely passed in — this owns the composition
 * (statement left, action right, marks and trust beneath) and nothing else.
 */
function ClosingBand({
  label = 'Get started with Gab AI',
  kicker,
  lines,
  lede,
  marks,
  trust,
  primary,
  secondary,
  showStores = true,
}) {
  const { c, band, isXS, isCompact, width, pad, display } = useLanding();
  const headline = display({ min: 34, max: 70, leading: 0.98, tracking: -0.042 });

  return (
    <View
      accessibilityRole={isWeb ? 'region' : undefined}
      accessibilityLabel={label}
      style={{
        width: '100%',
        alignSelf: 'stretch',
        alignItems: 'center',
        backgroundColor: band.bg,
        overflow: 'hidden',
        borderTopWidth: 1,
        borderTopColor: band.rule,
      }}
    >
      <View
        style={{
          width: '100%',
          maxWidth: LAYOUT.max,
          paddingHorizontal: pad,
          paddingVertical: fluid(width, 76, 132),
        }}
      >
        <Reveal>
          {kicker ? <Kicker tone="band">{kicker}</Kicker> : null}

          <DisplayHeading
            tag="h2"
            mask={false}
            type={headline}
            color={band.ink}
            style={{ marginTop: kicker ? fluid(width, 24, 36) : 0 }}
            lines={lines}
          />

          <View style={{ height: 1, backgroundColor: band.rule, marginTop: fluid(width, 30, 48) }} />

          <View
            style={{
              flexDirection: isCompact ? 'column' : 'row',
              gap: isCompact ? 32 : 56,
              alignItems: isCompact ? 'stretch' : 'flex-start',
              paddingTop: fluid(width, 26, 40),
            }}
          >
            <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0, gap: 24 }}>
              <Lede tone="band" max={430}>{lede}</Lede>

              {marks?.length ? (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
                  {marks.map((mark, index) => (
                    <React.Fragment key={mark}>
                      {index > 0 ? (
                        <View
                          aria-hidden
                          style={{ width: 3, height: 3, borderRadius: 2, backgroundColor: band.faint }}
                        />
                      ) : null}
                      <Text weight="bold" style={{ color: band.muted, fontSize: 12.5, letterSpacing: 0.2 }}>
                        {mark}
                      </Text>
                    </React.Fragment>
                  ))}
                </View>
              ) : null}
            </View>

            <View style={{ flex: isCompact ? undefined : 1, minWidth: 0, gap: 20 }}>
              <CtaRow>
                <PrimaryCta label={primary.label} href={primary.href} full={isXS} />
                {secondary ? (
                  <GhostCta
                    label={secondary.label}
                    href={secondary.href}
                    onPress={secondary.onPress}
                    icon={secondary.icon}
                    tone="band"
                  />
                ) : null}
              </CtaRow>

              {trust?.length ? (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
                  {trust.map((item) => (
                    <View key={item} style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                      <Icon name="check" size={9} color={c.accent} solid />
                      <Text style={{ color: band.muted, fontSize: 12.5 }}>{item}</Text>
                    </View>
                  ))}
                </View>
              ) : null}

              {showStores ? <StoreBadges /> : null}
            </View>
          </View>
        </Reveal>
      </View>
    </View>
  );
}

export default React.memo(ClosingBand);
